import _extends from "@babel/runtime/helpers/esm/extends";
import React from 'react';
import { Language } from '../../enums';
import { useResponsivePropValue } from '../../hooks';
import { useAmountFormatLanguage, useTranslations } from '../../providers/DesignSystemI18nProvider';
import { createDataTestId } from '../../utils';
import { useTextValue } from '../Text/TextContext';
import { NON_BREAKING_SPACE } from '../constants';
import { AMOUNT_FALLBACK_TEXT, AMOUNT_TRANSLATION_KEYS, StyleType } from './Amount.constants';
import { AmountWrapper, CurrencySymbol, FractionPart } from './Amount.styles';
export function Amount(_ref) {
  let {
    value,
    isFractionPartShown = true,
    isFractionPartBolded = false,
    isFractionPartResized = true,
    isValueColorized,
    minFractionDigits = 2,
    maxFractionDigits,
    testId,
    isLineThrough,
    styledAsTextTagName,
    periodSuffix,
    type = StyleType.CURRENCY,
    ...typeDependentProps
  } = _ref;
  const formatLanguage = useAmountFormatLanguage();
  const translate = useTranslations();
  const textAs = useTextValue();
  const textAsResolved = useResponsivePropValue(textAs);
  if (!formatLanguage) {
    throw new Error('Please use <DesignSystemI18nProvider value={language} />');
  }
  const getTimeFormatFromSecond = () => {
    if (typeof value !== 'undefined') {
      const tenMinutes = 600;
      const hour = tenMinutes * 6;
      const tenHours = 10 * hour;
      let stringStart = 15;
      if (value >= tenMinutes) {
        stringStart = 14;
      }
      if (value >= hour) {
        stringStart = 12;
      }
      if (value >= tenHours) {
        stringStart = 11;
      }
      const seconds = new Date(0);
      seconds.setSeconds(value);
      return seconds.toISOString().slice(stringStart, 19);
    }
  };
  const numberFormat = (() => {
    if (type === StyleType.DECIMAL) {
      return new Intl.NumberFormat(formatLanguage, {
        style: StyleType.DECIMAL,
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: maxFractionDigits
      });
    }
    if (type === StyleType.PERCENT) {
      return new Intl.NumberFormat(formatLanguage, {
        style: StyleType.PERCENT,
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: maxFractionDigits
      });
    }
    if (type === StyleType.COUNTDOWN) {
      return new Intl.NumberFormat(formatLanguage, {
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: maxFractionDigits
      });
    }
    const {
      currency,
      currencyDisplay = 'code'
    } = typeDependentProps;
    return new Intl.NumberFormat(formatLanguage, {
      style: StyleType.CURRENCY,
      currencyDisplay: currencyDisplay,
      currency: currency,
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits
    });
  })();
  const isPercentStyle = type === StyleType.PERCENT;
  const isTimeStyle = type === StyleType.COUNTDOWN;
  const numberParts = numberFormat.formatToParts(value).map(_ref2 => {
    let {
      value: valuePart
    } = _ref2;
    return valuePart;
  });
  let currencyCode;
  if (type === StyleType.CURRENCY) {
    currencyCode = numberParts.pop();
    numberParts.pop();
  }
  const minorPart = isTimeStyle ? getTimeFormatFromSecond() : numberParts.pop();
  const punctuation = isTimeStyle ? '' : numberParts.pop();
  const majorPart = isPercentStyle || isTimeStyle || typeof value === 'undefined' ? '' : numberParts.reduce((string, part) => {
    const partAsNumber = Number(part);
    if (Language.PL === formatLanguage && partAsNumber >= 1000) {
      return `${string}${Math.floor(partAsNumber / 1000)}${NON_BREAKING_SPACE}${(partAsNumber % 1000).toString().padStart(3, '0')}`;
    }
    return string + part;
  }, '');
  const isNegativeValue = value?.toString().charAt(0) === '-';
  if (typeof value === 'undefined') {
    return React.createElement(AmountWrapper, _extends({
      isValueColorized: false
    }, createDataTestId(testId), {
      "data-component": "Amount"
    }), AMOUNT_FALLBACK_TEXT);
  }
  return React.createElement(AmountWrapper, _extends({
    isValueColorized: Boolean(isValueColorized) && !isNegativeValue,
    isLineThrough: isLineThrough
  }, createDataTestId(testId), {
    "data-component": "Amount"
  }), majorPart, isFractionPartShown && React.createElement(FractionPart, {
    typographyVariant: styledAsTextTagName ?? textAsResolved,
    isFractionPartBolded: isFractionPartBolded,
    isFractionPartResized: isFractionPartResized
  }, punctuation, minorPart), type === StyleType.CURRENCY && currencyCode && React.createElement(CurrencySymbol, {
    typographyVariant: styledAsTextTagName ?? textAsResolved,
    isFractionPartBolded: isFractionPartBolded,
    isFractionPartResized: isFractionPartResized,
    isCurrencyPartResized: typeDependentProps.isCurrencyPartResized
  }, "\u2009", currencyCode, periodSuffix && `/${translate(AMOUNT_TRANSLATION_KEYS[periodSuffix])}`));
}